import { createSlice } from '@reduxjs/toolkit';
import { getInitialStartTime } from '../../utils/programUtils';

const initialState = {
    modalities: [],
    notifications: [],
    modalityFrequencies: []
};

const patientModalitiesSlice = createSlice({
    name: "patientEnrollmentModalities",
    initialState,
    reducers: {
        resetModalitiesState: (state) => {
            Object.assign(state, initialState);
        },
        setModality : (state, action) => {
            const { deviceId, deviceModalityId, ...rest } = action.payload;
            const existingModalityIndex = state.modalities.findIndex(mod => (mod.deviceId === deviceId && mod.deviceModalityId === deviceModalityId));
            const modInfo = {...rest};
            
            // remove enrollment rules when toggling off patch thresholds
            if (modInfo.upperLimit === 0 && modInfo.lowerLimit === 0 && deviceId === 1) {
                state.notifications = state.notifications.filter((not)=> not.modalityId !== modInfo.modalityId);
                state.modalityFrequencies = state.modalityFrequencies.filter((not)=> not.modalityId !== modInfo.modalityId);
            }


            if (existingModalityIndex !== -1) {
                const updatedMods = state.modalities.map((mod, index) => 
                    index === existingModalityIndex ? { ...mod, ...rest} : mod
                )
                state.modalities = updatedMods;
            } else {
                const newModality = { deviceId: deviceId, deviceModalityId: deviceModalityId, ...rest };
                state.modalities = [...state.modalities, newModality];
            }

        },
        removeModality : (state, action) => {
            const deviceId = action.payload;
            const updatedModalities = state.modalities.filter(item => item.deviceId !== deviceId);
            state.modalities = updatedModalities;
          
            // if removing a perin patch, remove all enrollment rules
            if (deviceId === 1) {
                state.notifications = [];
                state.modalityFrequencies = [];
            }
        },

        setModalitiesState: (state, action) => {
            const { programDevices, allDevices, modalityValues} = action.payload;
            let uniqueDeviceIds = [];
            if (programDevices.length >= 1) {
                programDevices.forEach(device => {
                    if (!uniqueDeviceIds.some(e => e.deviceId === device.deviceId)) {
                        uniqueDeviceIds.push(device);
                    }
                });
            };

            const modalityInfo = {
                deviceId: '',
                deviceModalityId: '',
                upperLimit: 0,
                lowerLimit: 0,
                targetValue: 0,
                isContinuous: false,
                startTime: getInitialStartTime(),
                frequency: 1,
                interval: "Day",
                isAlarmActive: false
            };

            let newModalities = [];
            let newNotifications = [];
            let newModalityFrequencies = [];
            // Cached patient modality values are Perin Patch–only; never apply them to third-party
            // devices that happen to share a modalityId (e.g. a BP cuff's pulse rate vs the Patch's
            // pulse oximetry both report modalityId 3).
            const PERIN_PATCH_DEVICE_ID = 1;
            uniqueDeviceIds.forEach((device)=> {
                // get all modalities for a device
                let modalitiesFound = allDevices.find(item => item.deviceId === device.deviceId);
                if (modalitiesFound) {
                    let modalities = modalitiesFound.modalities;
                    const isPerinPatch = device.deviceId === PERIN_PATCH_DEVICE_ID;
                    // if a modality is already listed in the program, save that data
                    // otherwise, save the modality with 0 in place of values
                    let modalityVals = modalities.map((mod) => {
                        let tempMod = {...modalityInfo};
                        tempMod['deviceModalityId'] = mod.deviceModalityId;
                        tempMod['deviceId'] = device.deviceId;
                        tempMod['modalityName'] = mod.modalityName;
                        tempMod['modalityId'] = mod.modalityId;
                        tempMod['modalityType'] = mod.modalityType;
                        tempMod['modalityTypeDisplayName'] = mod.modalityTypeDisplayName;

                        const useCachedValues = isPerinPatch && modalityValues.hasOwnProperty(mod.modalityId);
                        if (useCachedValues) {
                            let found = modalityValues[mod.modalityId];
                            tempMod['upperLimit'] = found.upperLimit;
                            tempMod['lowerLimit'] = found.lowerLimit;
                            tempMod['targetValue'] = found.targetValue;
                            tempMod['isContinuous'] = found.isContinuous;
                            tempMod['startTime'] = found.startTime;
                            tempMod['frequency'] = found.frequency;
                            tempMod['interval'] = found.interval;
                        }

                        let found=programDevices.find((element)=>element.deviceModalityId===mod.deviceModalityId);
                        if (found) {
                            // if patient doesnt have current enrollments with values for the current modality, add template or saved modalities
                            if (!useCachedValues) {
                                tempMod['upperLimit'] = found.upperLimit;
                                tempMod['lowerLimit'] = found.lowerLimit;
                                tempMod['targetValue'] = found.targetValue;    
                                tempMod['isAlarmActive'] = found.isAlarmActive;
                                tempMod['isContinuous'] = found.isContinuous;
                                tempMod['startTime'] = found.startTime;
                                tempMod['frequency'] = found.frequency;
                                tempMod['interval'] = found.interval;
                            }

                            // save rules if updating and modality has rules
                            if (found.hasOwnProperty("rules")) {
                                found.rules.forEach((rule) => {
                                    let tempRule = {...rule};
                                    if (tempRule.hasOwnProperty("updatedAt")) {
                                        delete tempRule["updatedAt"];
                                    }
                                    if (tempRule.hasOwnProperty("createdAt")) {
                                        delete tempRule["createdAt"];
                                    }
                                    if (rule.ruleType === "enrollment" && rule.actionType === "sendNotification") {
                                        newNotifications.push(tempRule);
                                    } else if (rule.ruleType === "enrollment" && rule.actionType === "increaseFrequency") {
                                        newModalityFrequencies.push(tempRule);
                                    }
                                })
                            }

                        }
                        // if there is no saved/template/current modality values, save with initial values (0,0,0)                       
                        return tempMod;
                    })
                    newModalities = [...newModalities, ...modalityVals];
                }
            })
            return {
                modalities: newModalities,
                notifications: newNotifications,
                modalityFrequencies: newModalityFrequencies
            };
        },
        setNotification: (state, action) => {
            const {key, value, modalityId} = action.payload;
            const index = state.notifications.findIndex(notification => notification.modalityId === modalityId);

            if (index !== -1) {
                const updatedNotifications = [...state.notifications];
                // Update the state with the given key, value pair
                updatedNotifications[index] = {
                    ...updatedNotifications[index], // Keep existing properties
                    [key]: value
                };
                state.notifications = updatedNotifications
            }
        },
        removeNotification: (state, action) => {
            const modalityId = action.payload;
            const tempNotifications = state.notifications.filter((notification)=>notification.modalityId !== modalityId);
            state.notifications = tempNotifications;
        },
        setModFrequency: (state, action) => {
            const {key, value, modalityId} = action.payload;
            const index = state.modalityFrequencies.findIndex(mod => mod.modalityId === modalityId);

            if (index !== -1) {
                const updatedModFrequencies = [...state.modalityFrequencies];
                // Update the state with the given key, value pair
                updatedModFrequencies[index] = {
                    ...updatedModFrequencies[index], // Keep existing properties
                    [key]: value
                };

                state.modalityFrequencies = updatedModFrequencies
            };
        },
        removeModFrequency: (state, action) => {
            const modalityId = action.payload;
            const tempModalityFrequencies = state.modalityFrequencies.filter((mod)=>mod.modalityId !== modalityId);
            state.modalityFrequencies = tempModalityFrequencies;
        },
        setStateValue: (state, action) => {
            const { key, value } = action.payload;
            state[key] = value;
        }
    }
});

export const { 
    resetModalitiesState, 
    setModality, 
    removeModality, 
    setModalitiesState, 
    removeModFrequency,
    setModFrequency,
    setNotification,
    removeNotification,
    setStateValue 
} = patientModalitiesSlice.actions;
  
  export default patientModalitiesSlice.reducer;